'use client';

import { useState, useEffect } from 'react';
import { useRouter, usePathname } from '@/compat/navigation';
import { ChevronDown, Plus, Check, Building2, Mail, X } from 'lucide-react';
import { getIconComponent } from '@/components/icon-picker';
import { useSpaceOptional } from '@/contexts/space-context';
import { API, useSpaces } from '@pilot/api';

interface Space {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  role: string;
}

interface Invitation {
  id: string;
  space: {
    id: string;
    name: string;
    slug: string;
    icon?: string;
    description?: string;
  };
  role: string;
  invitedBy: {
    email: string;
    fullName: string;
  };
  createdAt: string;
  expiresAt: string;
}

export function SpaceSwitcher() {
  const router = useRouter();
  const pathname = usePathname();
  const spaceContext = useSpaceOptional();
  const { data: spaces = [], isLoading: loadingSpaces } = useSpaces();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const loading = loadingSpaces;
  const [responding, setResponding] = useState<string | null>(null);

  const currentSpace = spaceContext?.space;
  const CurrentIcon = currentSpace ? getIconComponent(currentSpace.icon || 'folder') : Building2;

  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        const data = await API.invitations.getAll();
        setInvitations(data.invitations || []);
      } catch (error) {
        console.error('Failed to fetch invitations:', error);
      }
    };

    fetchInvitations();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-space-switcher]')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleSpaceSelect = (slug: string) => {
    setIsOpen(false);
    const newPath = pathname.replace(/\/spaces\/[^/]+/, `/spaces/${slug}`);
    router.push(newPath);
  };

  const handleAcceptInvitation = async (e: React.MouseEvent, invitationId: string, spaceSlug: string) => {
    e.stopPropagation();
    setResponding(invitationId);
    try {
      await API.invitations.accept(invitationId);

      setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
      setIsOpen(false);
      router.push(`/spaces/${spaceSlug}/overview`);
    } catch (err: any) {
      console.error('Failed to accept invitation:', err);
      alert(err.message || 'Failed to accept invitation');
    } finally {
      setResponding(null);
    }
  };

  const handleDeclineInvitation = async (e: React.MouseEvent, invitationId: string) => {
    e.stopPropagation();
    setResponding(invitationId);
    try {
      await API.invitations.decline(invitationId);

      setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
    } catch (error) {
      console.error('Failed to decline invitation:', error);
    } finally {
      setResponding(null);
    }
  };

  if (loading) {
    return (
      <div className="h-9 w-40 bg-muted animate-pulse rounded-md" />
    );
  }

  return (
    <div className="relative" data-space-switcher>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 py-2 rounded-md hover:bg-muted transition-colors min-w-[160px]"
      >
        <div className="relative w-6 h-6 rounded bg-primary/10 flex items-center justify-center text-primary">
          <CurrentIcon className="w-4 h-4" />
          {invitations.length > 0 && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-destructive rounded-full" />
          )}
        </div>
        <span className="font-medium truncate max-w-[120px]">
          {currentSpace?.name || 'Select Space'}
        </span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-popover border rounded-lg shadow-lg z-50 py-1">
          {invitations.length > 0 && (
            <>
              <div className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                <Mail className="w-3 h-3" />
                Invitations
              </div>
              <div className="max-h-48 overflow-y-auto">
                {invitations.map((invitation) => {
                  const SpaceIcon = getIconComponent(invitation.space.icon || 'folder');
                  const isResponding = responding === invitation.id;

                  return (
                    <div
                      key={invitation.id}
                      className="px-3 py-2 hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-amber-100 flex items-center justify-center text-amber-600">
                          <SpaceIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{invitation.space.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            Invited by {invitation.invitedBy.fullName}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-2 pl-11">
                        <button
                          onClick={(e) => handleAcceptInvitation(e, invitation.id, invitation.space.slug)}
                          disabled={isResponding}
                          className="flex-1 text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
                        >
                          {isResponding ? '...' : 'Accept'}
                        </button>
                        <button
                          onClick={(e) => handleDeclineInvitation(e, invitation.id)}
                          disabled={isResponding}
                          className="text-xs px-3 py-1.5 border rounded-md hover:bg-muted disabled:opacity-50 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t my-1" />
            </>
          )}

          <div className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Spaces
          </div>

          {spaces.length === 0 ? (
            <div className="px-3 py-4 text-center text-muted-foreground text-sm">
              <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No spaces yet
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              {spaces.map((space: Space) => {
                const SpaceIcon = getIconComponent(space.icon || 'folder');
                return (
                  <button
                    key={space.id}
                    onClick={() => handleSpaceSelect(space.slug)}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted transition-colors text-left"
                  >
                    <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                      <SpaceIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{space.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        /{space.slug}
                      </div>
                    </div>
                    {space.slug === currentSpace?.slug && (
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div className="border-t mt-1 pt-1">
            <button
              onClick={() => {
                setIsOpen(false);
                router.push('/spaces/create');
              }}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted transition-colors text-left text-primary"
            >
              <div className="w-8 h-8 rounded border-2 border-dashed border-primary/50 flex items-center justify-center">
                <Plus className="w-4 h-4" />
              </div>
              <span className="font-medium">Create New Space</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
